import { useState, useRef, useEffect } from 'react';

export default function ChatBox({ subjectId }) {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [subjectName, setSubjectName] = useState('');
  const chatEndRef = useRef(null);
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const recognition = SpeechRecognition ? new SpeechRecognition() : null;
  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [lastReply, setLastReply] = useState('');


  // Auto-scroll on new message
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ✅ Fetch subject name on mount
  useEffect(() => {
    const fetchSubjectName = async () => {
      try {
        const res = await fetch(`https://backend.scholarkart.in/api/subject/${subjectId}`);
        const data = await res.json();
        if (data.subjectName) {
          setSubjectName(data.subjectName);
        }
      } catch (err) {
        console.error('Error fetching subject name:', err);
      }
    };

    if (subjectId) fetchSubjectName();
  }, [subjectId]);


 const sendMessage = async (overrideMessage) => {
  const msgToSend = (typeof overrideMessage === 'string' ? overrideMessage : input || '').trim();
  if (!msgToSend) return;

  const userMessage = { role: 'user', content: msgToSend };
  setMessages(prev => [...prev, userMessage]);
  setInput('');
  setIsTyping(true);

  let botMessage = { role: 'assistant', content: '' };
  setMessages(prev => [...prev, botMessage]);

  try {
    const response = await fetch('https://backend.scholarkart.in/api/chat/stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: msgToSend, subjectId }),
    });

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let fullReply = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n').filter(line => line.trim());

      for (const line of lines) {
        if (line === 'data: [DONE]') {
          setIsTyping(false);
          speakReply(fullReply);
          return;
        }

        if (line.startsWith('data: ')) {
          const token = line.replace('data: ', '');
          fullReply += token;

          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              content: fullReply,
            };
            return updated;
          });
        }
      }
    }
  } catch (err) {
    console.error('Streaming error:', err);
    setMessages(prev => [
      ...prev,
      { role: 'assistant', content: '⚠️ Sorry, an error occurred while streaming.' },
    ]);
    setIsTyping(false);
  }
};


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startListening = () => {
  if (!recognition) return;

  recognition.lang = 'en-IN';
  recognition.interimResults = false;
  recognition.onresult = (event) => {
    const transcript = event.results[0][0].transcript;
    setInput(transcript); // set voice input as message
    setTimeout(() => {
        sendMessage(transcript);
    }, 300);
  };
  recognition.onerror = (err) => {
    console.error('Voice input error:', err);
  };
  recognition.start();
};



const speakReply = (text) => {
  setLastReply(text); // ✅ Save it for replay

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'en-IN';
  utterance.rate = 1;

  utterance.onstart = () => {
    setIsSpeaking(true);
    setIsPaused(false);
  };
  utterance.onend = () => setIsSpeaking(false);
  utterance.onerror = () => setIsSpeaking(false);

  utteranceRef.current = utterance;
  window.speechSynthesis.speak(utterance);
};

const relisten = () => {
  if (lastReply) {
    speakReply(lastReply);
  }
};

const stopSpeaking = () => {
  window.speechSynthesis.cancel();
  setIsSpeaking(false);
};

const pauseSpeaking = () => {
  if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
    window.speechSynthesis.pause();
    setIsPaused(true);
  }
};

const resumeSpeaking = () => {
  if (window.speechSynthesis.paused) {
    window.speechSynthesis.resume();
    setIsPaused(false);
  }
};

  return (
    <div className="w-full max-w-2xl mx-auto p-4 shadow-lg rounded-lg border border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-700">
      <span className="font-semibold truncate">
        💬 Ask Your {subjectName || 'Sva Learn'} Tutor
        </span>

      <div className="h-96 sm:h-[25rem] overflow-y-auto space-y-3 p-3 border rounded bg-gray-50 dark:bg-gray-800">
        {messages.map((msg, idx) => (
        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`px-4 py-2 rounded-lg max-w-[80%] text-sm ${
            msg.role === 'user'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white'
            }`}>
            {msg.content}
            {msg.role === 'assistant' && idx === messages.length - 1 && (
                <div className="mt-1 text-right">
                <button onClick={relisten} className="text-xs text-blue-500 hover:underline">🔁 Relisten</button>
                </div>
            )}
            </div>
        </div>
        ))}
        {isSpeaking && (
        <div className="flex items-center gap-2 text-sm text-blue-500 animate-pulse">
            🔊 Speaking…
            {isPaused ? (
            <button
                onClick={resumeSpeaking}
                className="text-green-600 text-xs font-semibold hover:underline"
            >
                ▶️ Resume
            </button>
            ) : (
            <button
                onClick={pauseSpeaking}
                className="text-yellow-500 text-xs font-semibold hover:underline"
            >
                ⏸ Pause
            </button>
            )}
            <button
            onClick={stopSpeaking}
            className="text-red-500 text-xs font-semibold hover:underline"
            >
            🛑 Stop
            </button>
        </div>
        )}
        {isTyping && (
          <div className="flex justify-between items-center px-3 py-2 border-b bg-blue-600 text-white overflow-hidden whitespace-nowrap">AI is typing…</div>
        )}
        <div ref={chatEndRef} />
      </div>

      <div className="mt-4 flex gap-2 items-center">
        <textarea
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Type your question here..."
            className="flex-grow p-2 border rounded resize-none text-sm dark:bg-gray-800 dark:text-white dark:border-gray-600"
        />

        {/* Mic Button - Compact */}
        <button
            onClick={startListening}
            className="p-2 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
            title="Speak"
        >
            <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-gray-700 dark:text-gray-300"
            fill="currentColor"
            viewBox="0 0 24 24"
            >
            <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 14 0h-2zM11 18v3h2v-3h-2z" />
            </svg>
        </button>

        {/* Send Button - Slimmed */}
        <button
        onClick={sendMessage}
        disabled={!input.trim()}
        className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
        title="Send"
        >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
        >
            <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 12h14M12 5l7 7-7 7"
            />
        </svg>
        </button>

        </div>

    </div>
  );
}
